import React, { useState } from 'react';
import { Zap, Power, Settings, AlertTriangle, CheckCircle, Clock, DollarSign } from 'lucide-react';
import { useApp } from '../contexts/AppContext';
import { formatCurrency, timeAgo } from '../utils/helpers';

const Automation = () => {
    const { automationSettings, setAutomationSettings, aiDecisions } = useApp();
    const [settings, setSettings] = useState(automationSettings);

    const handleToggleAutomation = () => {
        setSettings({ ...settings, enabled: !settings.enabled });
        setAutomationSettings({ ...settings, enabled: !settings.enabled });
    };

    const handleBudgetChange = (field, value) => {
        setSettings({
            ...settings,
            budgetAutonomy: {
                ...settings.budgetAutonomy,
                [field]: value,
            },
        });
    };

    const handleSaveSettings = () => {
        setAutomationSettings(settings);
        alert('Automation settings saved!');
    };

    const getDecisionStatusColor = (status) => {
        const colors = {
            executed: 'success',
            'in-progress': 'warning',
            'pending-approval': 'info',
            rejected: 'error',
        };
        return colors[status] || 'neutral';
    };

    return (
        <div className="animate-fadeIn">
            <div style={{ marginBottom: 'var(--space-8)' }}>
                <h1>Autonomous Campaign Management</h1>
                <p className="text-secondary">Configure AI automation rules and monitor autonomous decisions.</p>
            </div>

            {/* Master Control */}
            <div className="card card-gradient" style={{ marginBottom: 'var(--space-8)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                        <h2 style={{ marginBottom: 'var(--space-2)' }}>🤖 Automation Status</h2>
                        <p style={{ margin: 0, fontSize: 'var(--font-size-lg)', opacity: 0.9 }}>
                            {settings.enabled ? 'AI is actively managing your campaigns' : 'Automation is currently disabled'}
                        </p>
                    </div>
                    <button
                        className={`btn ${settings.enabled ? 'btn-secondary' : 'btn-primary'}`}
                        style={{ fontSize: 'var(--font-size-lg)', padding: 'var(--space-4) var(--space-8)' }}
                        onClick={handleToggleAutomation}
                    >
                        <Power size={24} />
                        {settings.enabled ? 'Disable' : 'Enable'} Automation
                    </button>
                </div>
            </div>

            {/* Automation Controls */}
            <div className="grid grid-cols-3 gap-6" style={{ marginBottom: 'var(--space-8)' }}>
                {/* Budget Autonomy */}
                <div className="card">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-4)' }}>
                        <DollarSign size={20} style={{ color: 'var(--color-success)' }} />
                        <h3 style={{ margin: 0 }}>Budget Autonomy</h3>
                    </div>

                    <div className="input-group">
                        <label className="checkbox-wrapper">
                            <input
                                type="checkbox"
                                className="checkbox"
                                checked={settings.budgetAutonomy.enabled}
                                onChange={(e) => handleBudgetChange('enabled', e.target.checked)}
                            />
                            <span>Enable budget optimization</span>
                        </label>
                    </div>

                    <div className="input-group">
                        <label className="input-label">Max Daily Change (%)</label>
                        <input
                            type="number"
                            className="input-field"
                            value={settings.budgetAutonomy.maxDailyChange}
                            onChange={(e) => handleBudgetChange('maxDailyChange', parseInt(e.target.value))}
                            disabled={!settings.budgetAutonomy.enabled}
                        />
                    </div>

                    <div className="input-group">
                        <label className="input-label">Monthly Budget Cap</label>
                        <input
                            type="number"
                            className="input-field"
                            value={settings.budgetAutonomy.monthlyBudgetCap}
                            onChange={(e) => handleBudgetChange('monthlyBudgetCap', parseInt(e.target.value))}
                            disabled={!settings.budgetAutonomy.enabled}
                        />
                    </div>

                    <div className="input-group">
                        <label className="input-label">Require approval over</label>
                        <input
                            type="number"
                            className="input-field"
                            value={settings.budgetAutonomy.requireApprovalOver}
                            onChange={(e) => handleBudgetChange('requireApprovalOver', parseInt(e.target.value))}
                            disabled={!settings.budgetAutonomy.enabled}
                        />
                    </div>
                </div>

                {/* Creative Autonomy */}
                <div className="card">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-4)' }}>
                        <Zap size={20} style={{ color: 'var(--color-primary)' }} />
                        <h3 style={{ margin: 0 }}>Creative Autonomy</h3>
                    </div>

                    <div className="input-group">
                        <label className="checkbox-wrapper">
                            <input
                                type="checkbox"
                                className="checkbox"
                                checked={settings.creativeAutonomy.enabled}
                                onChange={(e) => setSettings({
                                    ...settings,
                                    creativeAutonomy: { ...settings.creativeAutonomy, enabled: e.target.checked }
                                })}
                            />
                            <span>Enable creative testing</span>
                        </label>
                    </div>

                    <div className="input-group">
                        <label className="checkbox-wrapper">
                            <input
                                type="checkbox"
                                className="checkbox"
                                checked={settings.creativeAutonomy.canGenerateVariations}
                                onChange={(e) => setSettings({
                                    ...settings,
                                    creativeAutonomy: { ...settings.creativeAutonomy, canGenerateVariations: e.target.checked }
                                })}
                                disabled={!settings.creativeAutonomy.enabled}
                            />
                            <span>Generate variations of existing creatives</span>
                        </label>
                    </div>

                    <div className="input-group">
                        <label className="checkbox-wrapper">
                            <input
                                type="checkbox"
                                className="checkbox"
                                checked={settings.creativeAutonomy.canCreateNewConcepts}
                                onChange={(e) => setSettings({
                                    ...settings,
                                    creativeAutonomy: { ...settings.creativeAutonomy, canCreateNewConcepts: e.target.checked }
                                })}
                                disabled={!settings.creativeAutonomy.enabled}
                            />
                            <span>Create entirely new concepts</span>
                        </label>
                    </div>

                    <div className="input-group">
                        <label className="checkbox-wrapper">
                            <input
                                type="checkbox"
                                className="checkbox"
                                checked={settings.creativeAutonomy.requiresReview}
                                onChange={(e) => setSettings({
                                    ...settings,
                                    creativeAutonomy: { ...settings.creativeAutonomy, requiresReview: e.target.checked }
                                })}
                                disabled={!settings.creativeAutonomy.enabled}
                            />
                            <span>Require human review before publishing</span>
                        </label>
                    </div>
                </div>

                {/* Performance Thresholds */}
                <div className="card">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-4)' }}>
                        <Settings size={20} style={{ color: 'var(--color-warning)' }} />
                        <h3 style={{ margin: 0 }}>Performance Thresholds</h3>
                    </div>

                    <div className="input-group">
                        <label className="input-label">Pause if CTR below (%)</label>
                        <input
                            type="number"
                            step="0.1"
                            className="input-field"
                            value={settings.performanceThresholds.pauseIfCTRBelow}
                            onChange={(e) => setSettings({
                                ...settings,
                                performanceThresholds: { ...settings.performanceThresholds, pauseIfCTRBelow: parseFloat(e.target.value) }
                            })}
                        />
                    </div>

                    <div className="input-group">
                        <label className="input-label">Pause if ROAS below</label>
                        <input
                            type="number"
                            step="0.1"
                            className="input-field"
                            value={settings.performanceThresholds.pauseIfROASBelow}
                            onChange={(e) => setSettings({
                                ...settings,
                                performanceThresholds: { ...settings.performanceThresholds, pauseIfROASBelow: parseFloat(e.target.value) }
                            })}
                        />
                    </div>

                    <div className="input-group">
                        <label className="input-label">Scale if ROAS above</label>
                        <input
                            type="number"
                            step="0.1"
                            className="input-field"
                            value={settings.performanceThresholds.scaleIfROASAbove}
                            onChange={(e) => setSettings({
                                ...settings,
                                performanceThresholds: { ...settings.performanceThresholds, scaleIfROASAbove: parseFloat(e.target.value) }
                            })}
                        />
                    </div>

                    <button className="btn btn-primary" style={{ width: '100%', marginTop: 'var(--space-2)' }} onClick={handleSaveSettings}>
                        Save Settings
                    </button>
                </div>
            </div>

            {/* AI Decisions Log */}
            <div className="card">
                <h2 style={{ marginBottom: 'var(--space-6)' }}>AI Decision Log</h2>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                    {aiDecisions.map((decision) => (
                        <div
                            key={decision.id}
                            style={{
                                padding: 'var(--space-4)',
                                background: 'var(--color-bg-secondary)',
                                borderRadius: 'var(--radius-lg)',
                                borderLeft: `4px solid var(--color-${getDecisionStatusColor(decision.status)})`,
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', marginBottom: 'var(--space-3)' }}>
                                <div style={{ flex: 1 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
                                        <span className={`badge badge-${getDecisionStatusColor(decision.status)}`}>
                                            {decision.status}
                                        </span>
                                        <span className="badge badge-neutral">{decision.type}</span>
                                        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>
                                            {timeAgo(decision.timestamp)}
                                        </div>
                                    </div>
                                    <div style={{ fontWeight: 600, marginBottom: 'var(--space-1)' }}>
                                        {decision.action}
                                    </div>
                                    <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                                        {decision.reason}
                                    </p>
                                </div>
                            </div>

                            <div
                                style={{
                                    padding: 'var(--space-3)',
                                    background: 'var(--color-surface)',
                                    borderRadius: 'var(--radius-md)',
                                    marginBottom: 'var(--space-3)',
                                }}
                            >
                                <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-success)' }}>
                                    💡 Impact: {decision.impact}
                                </div>
                            </div>

                            {decision.canRevert && decision.status === 'executed' && (
                                <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                                    <button className="btn btn-sm btn-secondary">
                                        Revert Decision
                                    </button>
                                    <button className="btn btn-sm btn-ghost">
                                        View Details
                                    </button>
                                </div>
                            )}

                            {decision.status === 'pending-approval' && (
                                <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                                    <button className="btn btn-sm btn-primary">
                                        <CheckCircle size={14} />
                                        Approve
                                    </button>
                                    <button className="btn btn-sm btn-secondary">
                                        Reject
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default Automation;
